import {
  HeatSeries,
  ChemicalElementSymbol,
  NonElementQuantity,
} from "src/store/api/generatedApi";
import { Chip, Stack, useTheme } from "@mui/material";
import { pickColorById } from "src/utils/colors";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { sorted } from "helpers";

const CHIP_STYLES = {
  violation: {
    fontSize: "0.7rem",
    height: "20px",
    fontWeight: "fontWeightMedium",
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
    "& .MuiChip-label": {
      paddingX: "6px",
    },
  },
  statusChip: {
    fontSize: "0.7rem",
    height: "20px",
  },
} as const;

interface ChemistryViolationsCellProps {
  row: HeatSeries;
  t: TFunction;
  availableConstraints: string[];
  handleElementClick?: (
    element: ChemicalElementSymbol | NonElementQuantity
  ) => void;
  wrap?: boolean;
  finalSampleOnly: boolean;
}

type ViolationType = "aboveMax" | "belowMin" | "both";

const getViolationType = (
  weightPercent: number,
  weightPercentMin: number,
  weightPercentMax: number
): ViolationType | null => {
  if (weightPercent < weightPercentMin && weightPercent > weightPercentMax)
    return "both"; // only happens if constraints are set in a "bad" way
  if (weightPercent < weightPercentMin) return "belowMin";
  if (weightPercent > weightPercentMax) return "aboveMax";
  return null;
};

const formatViolationLabel = (
  constraintName: string,
  counts: Record<ViolationType, number>,
  finalSampleOnly: boolean
): string => {
  const parts: string[] = [];

  if (counts.aboveMax > 0) {
    parts.push(`${finalSampleOnly ? "" : counts.aboveMax}▲`);
  }

  if (counts.belowMin > 0) {
    parts.push(`${finalSampleOnly ? "" : counts.belowMin}▼`);
  }

  if (counts.both > 0) {
    parts.push(`${finalSampleOnly ? "" : counts.both}⚡`);
  }

  return `${constraintName}${finalSampleOnly ? "" : ":"} ${parts.join(" ")}`;
};

export const ChemistryViolationsCells = ({
  row,
  t,
  availableConstraints,
  handleElementClick,
  wrap,
  finalSampleOnly,
}: ChemistryViolationsCellProps) => {
  const theme = useTheme();
  const hasSamples = row.samples.length > 0;
  const samplesToInclude = useMemo(
    () =>
      sorted(row.samples, (sample) => sample.sample_timestamp).filter(
        (_, index) => !finalSampleOnly || index === row.samples.length - 1
      ),
    [row.samples, finalSampleOnly]
  );
  const violationCounts = useMemo(() => {
    return samplesToInclude.reduce<
      Record<string, Record<ViolationType, number>>
    >((acc, sample) => {
      sample.values.forEach((value) => {
        const violationType = getViolationType(
          value.weight_percent,
          value.weight_percent_min,
          value.weight_percent_max
        );
        if (violationType) {
          const counts = (acc[value.constraint_name] ??= {
            aboveMax: 0,
            belowMin: 0,
            both: 0,
          });
          counts[violationType]++;
        }
      });
      return acc;
    }, {});
  }, [samplesToInclude]);

  return (
    <Stack
      direction="row"
      gap={0.2}
      pt={0.5}
      alignItems="center"
      flexWrap={wrap ? "wrap" : "nowrap"}
    >
      {sorted(Object.entries(violationCounts), ([constraintName]) =>
        availableConstraints.indexOf(constraintName)
      ).map(([constraintName, count]) => {
        const colorIndex = availableConstraints.indexOf(constraintName);
        const color = pickColorById(colorIndex);

        return (
          <Chip
            key={constraintName}
            label={formatViolationLabel(constraintName, count, finalSampleOnly)}
            size="small"
            onClick={() =>
              handleElementClick?.(
                constraintName as ChemicalElementSymbol | NonElementQuantity
              )
            }
            sx={{
              backgroundColor: color,
              color: theme.palette.common.white,
              ...CHIP_STYLES.violation,
            }}
          />
        );
      })}
      {Object.keys(violationCounts).length === 0 && (
        <Chip
          label={t(hasSamples ? "noViolations" : "noChemistryData")}
          size="small"
          color={hasSamples ? "success" : "default"}
          variant="outlined"
          sx={CHIP_STYLES.statusChip}
        />
      )}
    </Stack>
  );
};
